import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { BASEMAP_URL, BASEMAP_ATTRIBUTION } from '../utils/basemap';
import { reverseGeocode, searchPlaces, PlaceSuggestion, LatLng } from '../lib/melqarto';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface LocationPickerProps {
  /**
   * `address` : l'adresse du point d'après Melqarto, à défaut les coordonnées
   * en texte — toujours renseignée, un point sans libellé ne s'affiche pas
   * dans l'app.
   */
  onLocationSelect: (lat: number, lng: number, address: string) => void;
  initialLat?: number;
  initialLng?: number;
  vehicleType?: string;
  onClose: () => void;
}

/**
 * Recentre la carte sur le point choisi hors de la carte (adresse trouvée,
 * « Ma position ») : un clic sur la carte, lui, n'a pas à la déplacer.
 */
const FlyTo: React.FC<{ target: LatLng | null }> = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 16), { duration: 0.6 });
  }, [map, target]);
  return null;
};

/** Centre courant de la carte, pour orienter la recherche d'adresse. */
const CenterTracker: React.FC<{ onChange: (center: LatLng) => void }> = ({ onChange }) => {
  const map = useMapEvents({
    moveend: () => { const c = map.getCenter(); onChange({ lat: c.lat, lng: c.lng }); }
  });
  useEffect(() => { const c = map.getCenter(); onChange({ lat: c.lat, lng: c.lng }); }, [map, onChange]);
  return null;
};

// Composant pour gérer les clics sur la carte
const MapClickHandler: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
  selectedPosition: { lat: number; lng: number } | null;
}> = ({ onLocationSelect, selectedPosition }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });

  return null;
};

// Composant pour les contrôles de carte personnalisés
const MapControls: React.FC<{
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReCenter: () => void;
  userPosition: { lat: number; lng: number } | null;
}> = ({ onZoomIn, onZoomOut, onReCenter, userPosition }) => {
  const map = useMap();

  const handleZoomIn = () => {
    map.zoomIn();
  };

  const handleZoomOut = () => {
    map.zoomOut();
  };

  const handleReCenter = () => {
    if (userPosition) {
      map.setView([userPosition.lat, userPosition.lng], 13);
    }
  };

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col space-y-2">
      {/* Bouton de recentrage */}
      <button
        onClick={handleReCenter}
        disabled={!userPosition}
        className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Se recentrer sur ma position"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      </button>

      {/* Bloc de zoom */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Zoom In */}
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors border-b border-gray-100"
          title="Zoom avant"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
        </button>
        
        {/* Zoom Out */}
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 transition-colors"
          title="Zoom arrière"
        >
          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationSelect,
  initialLat = 36.8065, // Tunis par défaut
  initialLng = 10.1815,
  vehicleType = 'car',
  onClose
}) => {
  const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(
    initialLat && initialLng ? { lat: initialLat, lng: initialLng } : null
  );
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  // Point à rejoindre en vol (adresse trouvée, « Ma position ») — distinct de
  // la sélection, qu'un clic sur la carte modifie sans déplacer la vue.
  const [flyTarget, setFlyTarget] = useState<LatLng | null>(null);
  const [mapCenterNow, setMapCenterNow] = useState<LatLng | null>(null);

  // Adresse du point sélectionné, d'après Melqarto : des coordonnées seules
  // ne disent pas si on a cliqué la bonne rue. `resolving` tient « Confirmer »
  // fermé le temps de la réponse : l'app mobile n'affiche que le libellé
  // (jamais les coordonnées), confirmer avant qu'il n'arrive enregistrait une
  // annonce placée mais « Emplacement indisponible ».
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  useEffect(() => {
    setSelectedAddress(null);
    if (!selectedPosition) { setResolvingAddress(false); return; }
    let cancelled = false;
    setResolvingAddress(true);
    reverseGeocode(selectedPosition.lat, selectedPosition.lng).then((address) => {
      if (cancelled) return;
      setSelectedAddress(address?.displayName ?? null);
      setResolvingAddress(false);
    });
    return () => { cancelled = true; };
  }, [selectedPosition]);

  // Recherche d'adresse (Melqarto), débattue : on interroge au calme, pas à
  // chaque touche, et une réponse en retard n'écrase pas la plus récente.
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  // Choisir une suggestion recopie son adresse dans le champ : ce n'est pas
  // une nouvelle recherche, la liste ne doit pas se rouvrir sur elle-même.
  const skipNextSearch = useRef(false);
  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    if (query.trim().length < 3) {
      setSuggestions([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchError(null);
      searchPlaces(query, mapCenterNow ?? undefined, controller.signal)
        .then((results) => { setSuggestions(results); setSuggestionsOpen(true); })
        .catch(() => {
          if (controller.signal.aborted) return;
          setSuggestions([]);
          setSearchError("La recherche d'adresse ne répond pas.");
        })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
    // Le centre de la carte oriente la recherche mais ne la relance pas : un
    // simple déplacement de la carte ne doit pas refaire une requête.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!suggestionsOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) setSuggestionsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [suggestionsOpen]);

  const chooseSuggestion = (suggestion: PlaceSuggestion) => {
    setSelectedPosition(suggestion.position);
    setFlyTarget(suggestion.position);
    skipNextSearch.current = suggestion.displayName !== query;
    setQuery(suggestion.displayName);
    setSuggestionsOpen(false);
  };

  // Fonction pour obtenir l'emoji du véhicule
  const getVehicleEmoji = (type: string) => {
    switch (type) {
      case 'car':
        return '🚗';
      case 'motorcycle':
        return '🏍️';
      case 'scooter':
        return '🛴';
      case 'bike':
        return '🚲';
      default:
        return '🚗';
    }
  };

  // Obtenir la position de l'utilisateur
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    }
  }, []);

  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedPosition({ lat, lng });
  };

  const handleConfirm = () => {
    if (!selectedPosition || resolvingAddress) return;
    // Melqarto muet (point en mer, service en panne) : les coordonnées font
    // office de libellé, comme dans l'app — jamais un champ vide.
    const address = selectedAddress ?? `${selectedPosition.lat.toFixed(6)}, ${selectedPosition.lng.toFixed(6)}`;
    onLocationSelect(selectedPosition.lat, selectedPosition.lng, address);
    onClose();
  };

  const handleUseMyLocation = () => {
    if (userPosition) {
      setSelectedPosition(userPosition);
      setFlyTarget(userPosition);
    }
  };

  // Déterminer la position initiale de la carte
  const mapCenter = selectedPosition || userPosition || { lat: initialLat, lng: initialLng };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full sm:h-[90vh] md:h-[85vh] lg:h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Sélectionner la position</h3>
              <p className="text-xs sm:text-sm text-gray-600">Cherchez une adresse ou cliquez sur la carte pour placer le marqueur</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-3 flex-shrink-0"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Recherche d'adresse — Melqarto */}
        <div ref={searchBoxRef} className="relative px-3 sm:px-4 py-2.5 border-b border-gray-200 bg-white">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setSuggestionsOpen(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSuggestionsOpen(false);
                if (e.key === 'Enter' && suggestions[0]) { e.preventDefault(); chooseSuggestion(suggestions[0]); }
              }}
              placeholder="Adresse, rue, lieu… (Tunisie)"
              aria-label="Rechercher une adresse"
              className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-9 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-400 focus:outline-none"
            />
            {searching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500" aria-hidden="true" />
            )}
          </div>
          {searchError && <p className="mt-1 text-xs text-red-600">{searchError}</p>}
          {suggestionsOpen && suggestions.length > 0 && (
            <ul className="absolute left-3 right-3 sm:left-4 sm:right-4 top-full z-[1100] mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {suggestions.map((suggestion) => (
                <li key={`${suggestion.position.lat},${suggestion.position.lng}`}>
                  <button
                    type="button"
                    onClick={() => chooseSuggestion(suggestion)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                  >
                    {suggestion.displayName}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestionsOpen && !searching && suggestions.length === 0 && query.trim().length >= 3 && !searchError && (
            <p className="mt-1 text-xs text-gray-500">Aucun lieu trouvé pour « {query.trim()} ».</p>
          )}
        </div>

        {/* Carte */}
        <div className="flex-1 relative">
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
            zoomControl={false}
            attributionControl={true}
          >
            
            {/* Contrôles de carte personnalisés */}
            <MapControls 
              onZoomIn={() => {}}
              onZoomOut={() => {}}
              onReCenter={() => {}}
              userPosition={userPosition}
            />
            <TileLayer attribution={BASEMAP_ATTRIBUTION} url={BASEMAP_URL} />
            
            {/* Marqueur de position sélectionnée */}
            {selectedPosition && (
              <Marker 
                position={[selectedPosition.lat, selectedPosition.lng]}
                icon={L.divIcon({
                  className: 'custom-selected-marker',
                  html: `
                    <div style="
                      position: relative;
                      width: 40px;
                      height: 40px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    ">
                      <!-- Anneau externe (le plus clair) -->
                      <div style="
                        position: absolute;
                        width: 40px;
                        height: 40px;
                        border-radius: 50%;
                        background: rgba(99, 102, 241, 0.2);
                        border: 2px solid rgba(99, 102, 241, 0.3);
                      "></div>
                      
                      <!-- Anneau intermédiaire -->
                      <div style="
                        position: absolute;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        background: rgba(99, 102, 241, 0.4);
                        border: 2px solid rgba(99, 102, 241, 0.5);
                      "></div>
                      
                      <!-- Cercle central (indigo très clair) -->
                      <div style="
                        position: absolute;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: #c7d2fe;
                        border: 2px solid #ddd6fe;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      ">
                        <div style="
                          color: white;
                          font-size: 16px;
                          font-weight: bold;
                          font-family: Arial, sans-serif;
                        ">${getVehicleEmoji(vehicleType)}</div>
                      </div>
                    </div>
                  `,
                  iconSize: [40, 40],
                  iconAnchor: [20, 20]
                })}
              />
            )}

            {/* Marqueur de position utilisateur */}
            {userPosition && (
              <Marker 
                position={[userPosition.lat, userPosition.lng]}
                icon={L.divIcon({
                  className: 'custom-user-marker',
                  html: `
                    <div style="
                      background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                      width: 32px;
                      height: 32px;
                      border-radius: 50% 50% 50% 0;
                      transform: rotate(-45deg);
                      border: 3px solid white;
                      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      position: relative;
                    ">
                      <div style="
                        transform: rotate(45deg);
                        color: white;
                        font-size: 16px;
                        font-weight: bold;
                        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
                      ">📍</div>
                    </div>
                  `,
                  iconSize: [32, 32],
                  iconAnchor: [16, 32]
                })}
              />
            )}

            <MapClickHandler 
              onLocationSelect={handleLocationSelect}
              selectedPosition={selectedPosition}
            />
            <FlyTo target={flyTarget} />
            <CenterTracker onChange={setMapCenterNow} />
          </MapContainer>
        </div>

        {/* Footer avec actions */}
        <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {userPosition && (
                <button
                  onClick={handleUseMyLocation}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <span>Ma position</span>
                </button>
              )}
              
              {selectedPosition && (
                <div className="text-sm text-gray-600 min-w-0">
                  <span className="font-medium">Position sélectionnée :</span>
                  <br />
                  {/* L'adresse d'abord, les coordonnées en appoint : c'est la rue
                      qu'on vérifie, pas six décimales. */}
                  {resolvingAddress ? (
                    <span className="block text-gray-500 text-xs sm:text-sm">Recherche de l'adresse…</span>
                  ) : selectedAddress ? (
                    <span className="block text-gray-800 text-xs sm:text-sm max-w-md">{selectedAddress}</span>
                  ) : (
                    <span className="block text-gray-500 text-xs sm:text-sm">Adresse inconnue : les coordonnées serviront de libellé.</span>
                  )}
                  <span className="font-mono text-xs text-gray-400">
                    {selectedPosition.lat.toFixed(6)}, {selectedPosition.lng.toFixed(6)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex space-x-2 sm:space-x-3 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedPosition || resolvingAddress}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {resolvingAddress ? 'Adresse…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker; 